import React, { useCallback, useEffect, useState } from 'react';
import { RightOutlined, DownOutlined } from '@ant-design/icons';
import sensorboxGroupDocumentationService from '../../services/sensorboxGroupDocumentationService';
import sensorboxDocumentationService from '../../services/sensorboxDocumentationService';
import DocumentsPanel from '../documentsPanel';
import toast from '../toast';

interface RollupBox {
  sensorbox_id: string;
  sensor_box_name: string;
  alias?: string;
  model_count: number;
  before_count: number;
  after_count: number;
  misc_count: number;
}

const countBadge = (count: number, label: string) => (
  <span className={`ar-badge ${count > 0 ? 'ar-badge-filled' : 'ar-badge-empty'}`}>
    {label} · {count}
  </span>
);

/** Per-component documentation for Asset Details — click a row to expand and
 * view/edit that sensor box's Model / Images / Misc inline. */
const AssetDocumentsRollup: React.FC<{ assetGroupId: string; disabled?: boolean }> = ({
  assetGroupId,
  disabled,
}) => {
  const [boxes, setBoxes] = useState<RollupBox[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const fetchRollup = useCallback(async () => {
    if (!assetGroupId) return;
    try {
      setLoading(true);
      setLoadError(null);
      const res = await sensorboxGroupDocumentationService.getRollup(assetGroupId);
      setBoxes(res?.data?.result?.boxes || []);
    } catch (error: any) {
      const message =
        error?.response?.data?.detail || 'Failed to load component documentation summary';
      setBoxes([]);
      setLoadError(typeof message === 'string' ? message : 'Failed to load component documentation summary');
      toast.error(typeof message === 'string' ? message : 'Failed to load component documentation summary');
    } finally {
      setLoading(false);
    }
  }, [assetGroupId]);

  useEffect(() => {
    fetchRollup();
  }, [fetchRollup]);

  const toggleExpand = (sensorboxId: string) => {
    setExpandedId((prev) => (prev === sensorboxId ? null : sensorboxId));
  };

  if (loading && boxes.length === 0) {
    return <div className="ar-empty">Loading…</div>;
  }

  if (loadError && boxes.length === 0) {
    return <div className="ar-empty ar-error">{loadError}</div>;
  }

  if (boxes.length === 0) {
    return <div className="ar-empty">No sensor boxes in this asset yet.</div>;
  }

  return (
    <div className="ar-list">
      {boxes.map((box) => {
        const isExpanded = expandedId === box.sensorbox_id;
        return (
          <div className={`ar-item ${isExpanded ? 'ar-item-expanded' : ''}`} key={box.sensorbox_id}>
            <button
              type="button"
              className="ar-row ar-row-toggle"
              onClick={() => toggleExpand(box.sensorbox_id)}
              aria-expanded={isExpanded}
            >
              <div className="ar-name">
                <span className="ar-chevron" aria-hidden>
                  {isExpanded ? <DownOutlined /> : <RightOutlined />}
                </span>
                {box.sensor_box_name}
                {box.alias && <span className="ar-alias"> ({box.alias})</span>}
              </div>
              <div className="ar-badges">
                {countBadge(box.model_count, 'Model')}
                {countBadge(box.before_count, 'Before')}
                {countBadge(box.after_count, 'After')}
                {countBadge(box.misc_count, 'Misc')}
              </div>
            </button>
            {isExpanded && (
              <div className="ar-expand-panel">
                <DocumentsPanel
                  entityId={box.sensorbox_id}
                  api={sensorboxDocumentationService}
                  disabled={!!disabled}
                  emptyModelLabel="No model images added yet for this component."
                  onChanged={fetchRollup}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default AssetDocumentsRollup;
